/**
 * HelpVideoTour — reusable Windows-style illustrated video tour player.
 *
 * Syncs slides to AudioGuide `currentSectionIdx` when `activeGuideId === guideId`.
 * Scenes can be hand-authored or built via `buildScenesFromNarration(guideId)`.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useAudioGuide, ListenButton } from '../common/AudioGuide';
import { getHelpNarration } from '../common/helpNarrations';
import { FRAME_MAP, Callout } from './helpTours/frames';
import { buildScenesFromNarration } from './helpTours/buildScenes';

export { buildScenesFromNarration } from './helpTours/buildScenes';

const FONT = '"Segoe UI", system-ui, -apple-system, sans-serif';
const DEFAULT_ACCENT = '#0369a1';

/* ------------------------------------------------------------------ */
/* Player                                                              */
/* ------------------------------------------------------------------ */

export default function HelpVideoTour({
  guideId,
  title = 'Video Tour',
  accent = DEFAULT_ACCENT,
  scenes: scenesProp,
  compact = false,
  showHeader = true,
  autoStart = false,
  onClose,
}) {
  const audio = useAudioGuide();
  const narration = useMemo(() => getHelpNarration(guideId) || [], [guideId]);

  const scenes = useMemo(() => {
    if (Array.isArray(scenesProp) && scenesProp.length) return scenesProp;
    return buildScenesFromNarration(guideId);
  }, [scenesProp, guideId]);

  const [slide, setSlide] = useState(0);
  const [manualOverride, setManualOverride] = useState(false);

  const scene = scenes[slide] || scenes[0];
  const Frame = (scene && FRAME_MAP[scene.frame]) || FRAME_MAP.hero;
  const isThisGuide = audio.activeGuideId === guideId;
  const playing = isThisGuide && audio.status === 'playing';

  // Keep slide in range when scenes change
  useEffect(() => {
    if (!scenes.length) return;
    setSlide((s) => Math.max(0, Math.min(s, scenes.length - 1)));
  }, [scenes.length]);

  // Sync slides to narration sections while this guide is active
  useEffect(() => {
    if (!isThisGuide || manualOverride || !scenes.length) return;
    if (typeof audio.currentSectionIdx === 'number') {
      setSlide(Math.max(0, Math.min(audio.currentSectionIdx, scenes.length - 1)));
    }
  }, [isThisGuide, audio.currentSectionIdx, manualOverride, scenes.length]);

  useEffect(() => {
    if (!autoStart || !audio.supported) return;
    if (!narration.length || !guideId) return;
    if (audio.activeGuideId === guideId) return;
    audio.startGuide({
      id: guideId,
      title,
      accent,
      sections: narration,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, guideId]);

  if (!scene || !scenes.length) {
    return (
      <div style={{ padding: 24, color: '#64748b', fontFamily: FONT, fontSize: 14 }}>
        No video tour scenes available for this guide.
      </div>
    );
  }

  const startFrom = (idx = 0) => {
    setManualOverride(false);
    setSlide(idx);
    if (!audio.supported || !narration.length) return;
    if (isThisGuide) {
      audio.playSection(idx);
    } else {
      audio.startGuide({
        id: guideId,
        title,
        accent,
        sections: narration,
      }, { fromSectionIdx: idx });
    }
  };

  const togglePlay = () => {
    if (playing) {
      audio.pause();
      return;
    }
    if (isThisGuide && audio.status === 'paused') {
      setManualOverride(false);
      audio.play();
      return;
    }
    startFrom(slide);
  };

  const goSlide = (idx) => {
    const next = Math.max(0, Math.min(idx, scenes.length - 1));
    setSlide(next);
    if (isThisGuide && (audio.status === 'playing' || audio.status === 'paused')) {
      setManualOverride(false);
      audio.playSection(next);
    } else {
      setManualOverride(true);
    }
  };

  const height = compact ? 420 : 520;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 12,
      fontFamily: FONT,
    }}>
      {showHeader && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: accent, textTransform: 'uppercase',
            }}>
              Video tour
            </div>
            <h3 style={{ margin: '4px 0 0', fontSize: '1.15rem', color: '#1a1a1a' }}>
              {title}
            </h3>
            <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b', maxWidth: 560 }}>
              Illustrated walkthrough with Clara and Liam — follow chapters while the narration plays.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ListenButton guideId={guideId} title={title} accent={accent} />
            {onClose && (
              <button type="button" onClick={onClose} style={btnGhost}>Close</button>
            )}
          </div>
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: compact ? '1fr' : '220px 1fr',
        gap: 12,
        minHeight: height,
      }}>
        {!compact && (
          <div style={{
            background: '#f8f8f8', border: '1px solid #e0e0e0', borderRadius: 6,
            overflow: 'auto', maxHeight: height + 40, padding: 8,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', padding: '4px 8px 8px' }}>
              CHAPTERS
            </div>
            {scenes.map((s, i) => {
              const active = i === slide;
              return (
                <button
                  key={s.id || i}
                  type="button"
                  onClick={() => goSlide(i)}
                  style={{
                    display: 'flex', gap: 8, width: '100%', textAlign: 'left',
                    padding: '8px 8px', borderRadius: 4, border: 'none', cursor: 'pointer',
                    background: active ? `${accent}18` : 'transparent',
                    color: active ? accent : '#333',
                    marginBottom: 2, fontFamily: FONT,
                  }}
                >
                  <span style={{
                    width: 20, height: 20, borderRadius: 3, flexShrink: 0,
                    background: active ? accent : '#c8c8c8', color: 'white',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 800, marginTop: 1,
                  }}>{i + 1}</span>
                  <span>
                    <div style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.25 }}>{s.title}</div>
                    <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{s.blurb}</div>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ position: 'relative', height, borderRadius: 8, overflow: 'hidden' }}>
            <Frame accent={accent} title={scene.title} />
            {(scene.callouts || []).map((c) => (
              <Callout key={`${c.label}-${c.x}`} label={c.label} x={c.x} y={c.y} accent={accent} />
            ))}
            <div style={{
              position: 'absolute', left: 12, bottom: 12, right: 12,
              background: 'rgba(26,26,26,0.88)', color: 'white', borderRadius: 6,
              padding: '10px 12px', backdropFilter: 'blur(4px)', fontFamily: FONT,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span>{scene.icon}</span>
                <strong style={{ fontSize: 13 }}>{scene.title}</strong>
                <span style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.75 }}>
                  {slide + 1} / {scenes.length}
                </span>
              </div>
              <div style={{ fontSize: 12, opacity: 0.92, lineHeight: 1.4 }}>
                {isThisGuide && audio.currentSentence
                  ? audio.currentSentence
                  : scene.blurb}
              </div>
              {isThisGuide && audio.currentSpeaker && (
                <div style={{ marginTop: 6, fontSize: 11, color: '#7dd3fc' }}>
                  {audio.currentSpeaker === 'liam' ? 'Liam' : 'Clara'} speaking
                </div>
              )}
            </div>
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            background: '#1f2937', color: 'white', borderRadius: 6, padding: '10px 12px',
          }}>
            <button type="button" onClick={() => goSlide(slide - 1)} disabled={slide === 0} style={ctrlBtn}>⏮</button>
            <button type="button" onClick={togglePlay} style={{ ...ctrlBtn, background: accent, minWidth: 88 }}>
              {playing ? '⏸ Pause' : '▶ Play tour'}
            </button>
            <button
              type="button"
              onClick={() => goSlide(slide + 1)}
              disabled={slide >= scenes.length - 1}
              style={ctrlBtn}
            >⏭</button>
            <div style={{ flex: 1, minWidth: 120, height: 6, background: '#374151', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                width: `${((slide + 1) / scenes.length) * 100}%`,
                height: '100%', background: '#38bdf8', transition: 'width 0.25s ease',
              }} />
            </div>
            {compact && (
              <select
                value={slide}
                onChange={(e) => goSlide(Number(e.target.value))}
                style={{
                  background: '#374151', color: 'white', border: '1px solid #4b5563',
                  borderRadius: 3, padding: '4px 8px', fontSize: 11, maxWidth: 180, fontFamily: FONT,
                }}
              >
                {scenes.map((s, i) => (
                  <option key={s.id || i} value={i}>{i + 1}. {s.title}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const ctrlBtn = {
  background: '#374151', color: 'white', border: '1px solid #4b5563',
  borderRadius: 4, padding: '8px 12px', cursor: 'pointer', fontWeight: 700, fontSize: 13,
  fontFamily: FONT,
};

const btnGhost = {
  background: '#f5f5f5', border: '1px solid #d0d0d0', borderRadius: 4,
  padding: '6px 12px', cursor: 'pointer', fontWeight: 600, color: '#333',
  fontFamily: FONT,
};

/** Full-screen modal wrapper for Learning Center / Help Center */
export function HelpVideoTourModal({
  isOpen,
  onClose,
  guideId,
  title,
  accent = DEFAULT_ACCENT,
  scenes,
  autoStart = false,
}) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 10050 }}
      />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '94vw', maxWidth: 1100, maxHeight: '92vh', overflow: 'auto',
        background: 'white', borderRadius: 8, zIndex: 10051, padding: 20,
        boxShadow: '0 25px 60px rgba(0,0,0,0.35)', fontFamily: FONT,
      }}>
        <HelpVideoTour
          guideId={guideId}
          title={title}
          accent={accent}
          scenes={scenes}
          onClose={onClose}
          autoStart={autoStart}
        />
      </div>
    </>
  );
}
